import type { ISortDirection } from '@app/lib/sorting'
import type { AppAction } from '@app/state'

export interface State {
  isLaunchIntakeModalOpen: boolean
  launchIntakeErrorMessage?: string
  locationFilter?: string
  mrnFilter?: string
  pageNumber: number
  patientNameFilter?: string
  recordsPerPage: number
  sortDirection?: ISortDirection
  sortField?: string
  statusFilter: string[]
  typeFilter: string[]
}

export const initialState: State = {
  isLaunchIntakeModalOpen: false,
  locationFilter: null,
  mrnFilter: null,
  pageNumber: 1,
  patientNameFilter: null,
  recordsPerPage: 50,
  statusFilter: [],
  typeFilter: [],
}

export enum ActionType {
  CloseLaunchIntakeModal = 'VISIT_LIST/LAUNCH_INTAKE_MODAL/CLOSE',
  LaunchIntake = 'VISIT_LIST/LAUNCH_INTAKE',
  LaunchIntakeFailed = 'VISIT_LIST/LAUNCH_INTAKE/FAILED',
  OpenLaunchIntakeModal = 'VISIT_LIST/LAUNCH_INTAKE_MODAL/OPEN',
  SetLocationFilter = 'VISIT_LIST/SET_LOCATION_FILTER',
  SetMrnFilter = 'VISIT_LIST/SET_MRN_FILTER',
  SetPatientNameFilter = 'VISIT_LIST/SET_PATIENT_NAME_FILTER',
  SetPageNumber = 'VISIT_LIST/SET_PAGE_NUMBER',
  SetRecordsPerPage = 'VISIT_LIST/SET_RECORDS_PER_PAGE',
  SetStatusFilter = 'VISIT_LIST/SET_STATUS_FILTER',
  SetTypeFilter = 'VISIT_LIST/SET_TYPE_FILTER',
  SetSort = 'VISIT_LIST/SET_SORT',
  SetState = 'VISIT_LIST/SET_STATE',
}

export type Action =
  | { type: ActionType.CloseLaunchIntakeModal }
  | {
      type: ActionType.LaunchIntake
      visitId: string
      selectedInteractionIds: string[]
    }
  | { type: ActionType.LaunchIntakeFailed; errorMessage: string }
  | { type: ActionType.OpenLaunchIntakeModal }
  | { type: ActionType.SetLocationFilter; value?: string }
  | { type: ActionType.SetMrnFilter; value?: string }
  | { type: ActionType.SetPatientNameFilter; value?: string }
  | { type: ActionType.SetPageNumber; value: number }
  | { type: ActionType.SetRecordsPerPage; value: number }
  | { type: ActionType.SetStatusFilter; value: string[] }
  | { type: ActionType.SetSort; field?: string; direction?: ISortDirection }
  | { type: ActionType.SetTypeFilter; value: string[] }
  | { type: ActionType.SetState; state: State }

export const actions = {
  closeLaunchIntakeModal() {
    return { type: ActionType.CloseLaunchIntakeModal }
  },
  launchIntake(visitId: string, selectedInteractionIds: string[]) {
    return {
      type: ActionType.LaunchIntake,
      visitId,
      selectedInteractionIds,
    }
  },
  launchIntakeFailed(errorMessage: string) {
    return { type: ActionType.LaunchIntakeFailed, errorMessage }
  },
  openLaunchIntakeModal() {
    return { type: ActionType.OpenLaunchIntakeModal }
  },
  setLocationFilter(value?: string) {
    return { type: ActionType.SetLocationFilter, value }
  },
  setMrnFilter(value?: string) {
    return { type: ActionType.SetMrnFilter, value }
  },
  setPatientNameFilter(value?: string) {
    return { type: ActionType.SetPatientNameFilter, value }
  },
  setPageNumber(value: number) {
    return { type: ActionType.SetPageNumber, value }
  },
  setRecordsPerPage(value: number) {
    return { type: ActionType.SetRecordsPerPage, value }
  },
  setStatus(value: string[]) {
    return { type: ActionType.SetStatusFilter, value }
  },
  setSort(field?: string, direction?: string) {
    return { type: ActionType.SetSort, field, direction }
  },
  setType(value: string[]) {
    return { type: ActionType.SetTypeFilter, value }
  },
  setState(state: State) {
    return { type: ActionType.SetState, state }
  },
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.SetLocationFilter:
      return {
        ...state,
        locationFilter: action.value,
      }
    case ActionType.SetMrnFilter:
      return {
        ...state,
        mrnFilter: action.value,
      }
    case ActionType.SetPatientNameFilter:
      return {
        ...state,
        patientNameFilter: action.value,
      }
    case ActionType.SetPageNumber:
      return {
        ...state,
        pageNumber: action.value,
      }
    case ActionType.SetRecordsPerPage:
      return {
        ...state,
        recordsPerPage: action.value,
      }
    case ActionType.SetStatusFilter:
      return {
        ...state,
        statusFilter: action.value,
      }
    case ActionType.SetSort:
      return {
        ...state,
        sortField: action.field,
        sortDirection: action.direction,
      }
    case ActionType.SetTypeFilter:
      return {
        ...state,
        typeFilter: action.value,
      }
    case ActionType.OpenLaunchIntakeModal:
      return {
        ...state,
        isLaunchIntakeModalOpen: true,
        launchIntakeErrorMessage: null,
      }
    case ActionType.CloseLaunchIntakeModal:
      return {
        ...state,
        isLaunchIntakeModalOpen: false,
        launchIntakeErrorMessage: null,
      }

    case ActionType.LaunchIntakeFailed:
      return {
        ...state,
        launchIntakeErrorMessage: action.errorMessage,
      }

    case ActionType.SetState:
      return action.state

    default:
      return state
  }
}
